import type {
  CloseReason,
  Occurrence,
  VulnStatus,
  VulnTimelineEvent,
} from '../../../modules/types/vulnerabilities'
import { REOPENS_ON_DETECTION, reopenHintKey } from './statusPicker'

/**
 * Helpers for the timeline-as-occurrence-history view.
 *
 * Since §7, a `detected` event carries its evidence occurrence nested under
 * `occurrence`, resolved server-side per page — there is no client-side join
 * against `detail.occurrences`. `occurrence` is null on non-detected events and
 * on detected events whose occurrence was later deleted.
 */

/** The occurrence a timeline row can show, or null if the row has no evidence. */
export function eventOccurrence(event: VulnTimelineEvent): Occurrence | null {
  return event?.occurrence ?? null
}

/**
 * A stable per-row key.
 *
 * NOTE: the timeline row shape exposes no id of its own, so the §3 "dedupe by
 * id" rule cannot be applied literally here. This composite is the best natural
 * key available: type + timestamp + scan + the payload id the event refers to.
 * Two genuinely distinct events would have to share all four to collide.
 */
export function timelineEventKey(event: VulnTimelineEvent): string {
  const payloadId =
    (typeof event.payload?.occurrence_id === 'string' && event.payload.occurrence_id) ||
    (typeof event.payload?.comment_id === 'string' && event.payload.comment_id) ||
    ''
  return [event.event_type, event.created_at, event.scan_id ?? '', payloadId].join('|')
}

/** Append timeline pages, dropping rows already rendered (§3: shifting boundaries). */
export function mergeTimelinePages(pages: VulnTimelineEvent[][] = []): VulnTimelineEvent[] {
  const seen = new Set<string>()
  const merged: VulnTimelineEvent[] = []
  for (const event of pages.flat()) {
    if (!event) continue
    const key = timelineEventKey(event)
    if (seen.has(key)) continue
    seen.add(key)
    merged.push(event)
  }
  return merged
}

/** One end of a status change, as `finding_status._apply` recorded it. */
export interface TimelineStatusRef {
  /** Null when the payload carries a value outside the current vocabulary. */
  status: VulnStatus | null
  closeReason: CloseReason | null
  /** The payload string as written, so an unrecognised value is still shown. */
  raw: string
}

/**
 * A `status_changed` event, read rather than guessed at.
 *
 * `from_status`/`to_status` are only half of it: both `resolved` and
 * `false_positive` collapse into `closed` under the Finding contract, so
 * "changed status from open to closed" no longer says WHY — the reason does,
 * and `finding_status._apply` writes it as `from_close_reason`/`to_close_reason`
 * beside the statuses.
 *
 * `automatic` is the other half. `finding_reconcile._close_agreed` closes a
 * finding with `actor=None` and a `source` of `{scan_id, sensors}` — no `reason`
 * key at all — so an actor-less event is NOT necessarily the `redetected`
 * reopen, and a renderer that assumes it is falls through to the "{email}
 * changed status…" string with `email` undefined. ICU renders that as an empty
 * string, which reads as an anonymous, reason-less row.
 */
export interface TimelineStatusChange {
  from: TimelineStatusRef
  to: TimelineStatusRef
  /** `source.reason`; `redetected` on the automatic reopen, else empty. */
  reason: string
  /** No actor: a scan drove this, not a person. */
  automatic: boolean
  /**
   * What a later detection will do to a finding closed this way — the same hint
   * the StatusPicker shows for the current state, from the same policy table.
   * Null unless `to` is a close whose reason is known.
   */
  hintKey: string | null
}

const CLOSE_REASONS: readonly string[] = Object.keys(REOPENS_ON_DETECTION)

function asCloseReason(value: unknown): CloseReason | null {
  return typeof value === 'string' && CLOSE_REASONS.includes(value)
    ? (value as CloseReason)
    : null
}

function statusRef(status: unknown, closeReason: unknown): TimelineStatusRef {
  return {
    status: status === 'open' || status === 'closed' ? status : null,
    closeReason: asCloseReason(closeReason),
    raw: String(status ?? ''),
  }
}

export function timelineStatusChange(event: VulnTimelineEvent): TimelineStatusChange {
  const payload = event.payload ?? {}
  const to = statusRef(payload.to_status, payload.to_close_reason)
  return {
    from: statusRef(payload.from_status, payload.from_close_reason),
    to,
    reason: typeof payload.reason === 'string' ? payload.reason : '',
    automatic: event.actor == null,
    hintKey: to.status === 'closed' && to.closeReason ? reopenHintKey(to.closeReason) : null,
  }
}

/**
 * The row to select on open: the newest event that actually carries evidence.
 * Events arrive newest-first, so the first one with an occurrence wins.
 */
export function defaultSelectedKey(events: VulnTimelineEvent[]): string | null {
  for (const event of events ?? []) {
    if (eventOccurrence(event)) return timelineEventKey(event)
  }
  return null
}
